import { Typography } from '@mui/material';
import React, { ReactElement, ReactNode, useState } from 'react';
import { ValidationRulesType } from '../../../production/helpers/productionUtils';
import I18n from '../../i18n/I18n';
import { H, V } from '../../Layout';
import { useStyling } from '../../Theme';
import FormButtons from '../FormButtons';
import { FormProvider, Listener } from '../FormContext';
import FormWidget from '../FormWidget';

type TokenValuesType = {
    [key: string]: string | number | boolean;
};

type InlineFormEditorProps = {
    titleToken: string;
    titleTokenValues?: TokenValuesType;
    validationRules?: ValidationRulesType;
    onSave: (state) => void;
    onCancel: () => void;
    children?: ReactNode;
    inputFields?: ReactElement;
    listeners?: Listener[];
    initialState?: any;
    checkboxLabelToken?: string;
    checkboxName?: string;
    isSaving?: boolean;
    saveButtonLabel?: string;
    hideCheckbox?: boolean;
    disableSaveButton?: boolean;
    showActions?: boolean;
};

export default function InlineFieldsEditor({
    titleToken,
    titleTokenValues = undefined,
    validationRules,
    children = undefined,
    inputFields = undefined,
    onSave,
    onCancel,
    listeners = [],
    initialState = null,
    checkboxLabelToken = undefined,
    checkboxName = undefined,
    isSaving = false,
    saveButtonLabel = 'dialog.add',
    hideCheckbox = false,
    disableSaveButton = false,
    showActions = true
}: InlineFormEditorProps) {
    const { theme } = useStyling();

    const [state, setState] = useState(initialState);

    return (
        <V
            sx={{
                border: `1px solid ${theme.palette.focus.dark}`,
                borderRadius: '12px',
                backgroundColor: theme.palette.focus.main,
                padding: 2,
                '& .MuiInputBase-root': {
                    backgroundColor: theme.palette.background.paper
                },
                '& ._lexicalContainer': {
                    backgroundColor: theme.palette.background.paper
                }
            }}
        >
            <Typography
                color="text.primary"
                fontWeight={theme.typography.fontWeightBold}
                textAlign={'start'}
                width={'100%'}
                sx={{ px: 2 }}
            >
                <I18n token={titleToken} values={titleTokenValues} />
            </Typography>
            <FormProvider state={state} setState={setState} validationRules={validationRules} listeners={listeners}>
                <H gap={2} sx={{ width: '100%', mt: 2 }}>
                    {inputFields || children}
                </H>
                <H sx={{ justifyContent: checkboxLabelToken && !hideCheckbox ? 'space-between' : 'flex-end', px: 2 }}>
                    {checkboxLabelToken && !hideCheckbox && (
                        <FormWidget
                            component="Checkbox"
                            name={checkboxName}
                            label={<I18n token={checkboxLabelToken} />}
                        />
                    )}

                    {showActions && (
                        <H gap={2} sx={{ justifyContent: 'flex-end', width: 'fit-content' }}>
                            <FormButtons
                                onClickCancel={onCancel}
                                onClickSave={() => onSave(state)}
                                saveButtonLabel={saveButtonLabel}
                                disableSave={disableSaveButton}
                                isSaving={isSaving}
                            />
                        </H>
                    )}
                </H>
            </FormProvider>
        </V>
    );
}
